import { HonestState, MetricTile } from "../components.js";
import type {
  FrictionDerivation,
  Metric,
  ModuleCapabilityValue,
  ReliabilityDerivation,
} from "./projectMetrics.js";

/**
 * E8.7 (NX-2632) — the capability, reliability and friction panels on Project detail.
 *
 * GET /projects/:id has returned all three fields since NX-2359; reliability and friction are real
 * derivations (E5.7/E5.6, reconstructed from derived_fact rows written by the trusted worker) while
 * capability is an honest N/A until E5.5 derives it. This module is their only consumer.
 *
 * Every panel here is presentational: it takes its slice of the ALREADY-fetched envelope as props
 * and never fetches (AC4). Nothing infers a value the API withheld — an `n/a` metric renders the
 * API's own reason string, so a metric that is merely unobserved can never read as a score of zero
 * (ADR-016). That also makes the panels correct under role projection without a second opinion: if
 * a role's projection drops a field, the panel renders the honest state rather than a client-side
 * fallback that fabricates one.
 *
 * On AC1's "MetricTile with its own ConfidencePill": MetricTile ALREADY renders the confidence for
 * the figure it shows (its `.mc` line — pip + percentage, or "Confidence N/A"), and that is the
 * convention every other metric on this screen uses. Adding a separate ConfidencePill beside it
 * would render the same number twice, which is the duplication AC5 forbids. So the confidence is
 * passed to MetricTile rather than drawn again.
 */

const DAY_SECONDS = 86_400;
const HOUR_SECONDS = 3_600;

const decimal = (value: number, places = 1): string => String(Number(value.toFixed(places)));
const metricConfidence = <T,>(metric: Metric<T>): number | null =>
  metric.kind === "value" && metric.confidence.kind === "value"
    ? metric.confidence.confidence
    : null;
const metricReason = <T,>(metric: Metric<T>, fallback: string): string =>
  metric.kind === "n/a" ? metric.reason : fallback;

/**
 * One metric: its tile when the API derived a value, the honest state when it did not. Every panel
 * below goes through this, so "absent renders as N/A, never as zero" holds in exactly one place
 * rather than being re-decided per metric. Exported so NX-2631's BlendedDoraPanel (ProgramRollup.tsx)
 * reuses the identical honest-state/tile split rather than a second copy of the same decision.
 *
 * `onClick` (NX-2634/E13.8) is an OPT-IN drill-to-evidence affordance: when supplied, a rendered
 * (value) tile becomes a keyboard-and-pointer button that opens the caller's evidence drawer, mirroring
 * Project.tsx's `DimensionTile` click pattern. Omitted by every existing caller (Capability/Friction/
 * Reliability), so this is additive — never changes their rendered output.
 */
export function DerivedMetric<T>({
  label,
  metric,
  render,
  kind = "nodata",
  onClick,
  signal,
}: {
  label: string;
  metric: Metric<T>;
  render: (value: T) => React.ReactNode;
  kind?: "nodata" | "notobs";
  onClick?: () => void;
  /** NX-2802 — extracts the raw number for signal banding; N/A metrics never reach it (AC4). */
  signal?: (value: T) => number;
}): React.ReactNode {
  if (metric.kind !== "value") {
    return <HonestState kind={kind} title={label} detail={metricReason(metric, "N/A")} />;
  }
  const tile = (
    <MetricTile
      label={label}
      value={render(metric.value)}
      confidence={metricConfidence(metric)}
      signal={signal?.(metric.value)}
    />
  );
  if (!onClick) return tile;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${label} evidence drawer`}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: "pointer" }}
    >
      {tile}
    </div>
  );
}

// --- Friction (E5.6) -------------------------------------------------------
export function FrictionPanel({ friction }: { friction: FrictionDerivation }): React.ReactNode {
  return (
    <>
      <div className="sectitle">
        <h3 className="sec">DevEx friction</h3>
        <span className="cnt">
          CI wait · flaky retries · review wait · dependency wait · lost capacity
        </span>
      </div>
      <div className="grid cols-3">
        <DerivedMetric
          label="CI queue wait"
          metric={friction.ciWait}
          render={(v) => (
            <>
              {decimal(v.meanSeconds / HOUR_SECONDS)} <small>h mean</small>
            </>
          )}
        />
        <DerivedMetric
          label="Flaky retry cost"
          metric={friction.flakyRetryCost}
          render={(v) => (
            <>
              {decimal(v.totalSeconds / HOUR_SECONDS)} <small>h over {v.chains} chains</small>
            </>
          )}
        />
        <DerivedMetric
          label="Review wait"
          metric={friction.reviewWait}
          signal={(v) => v.meanSeconds / HOUR_SECONDS}
          render={(v) => (
            <>
              {decimal(v.meanSeconds / HOUR_SECONDS)} <small>h mean</small>
            </>
          )}
        />
        <DerivedMetric
          label="Dependency wait"
          metric={friction.dependencyWait}
          render={(v) => (
            <>
              {decimal(v.meanSeconds / HOUR_SECONDS)} <small>h mean</small>
            </>
          )}
        />
        <DerivedMetric
          label="Lost capacity"
          metric={friction.lostCapacity}
          render={(v) => (
            <>
              {decimal(v.items, 2)} <small>items</small>
            </>
          )}
        />
      </div>
    </>
  );
}

// --- Reliability (E5.7) ----------------------------------------------------
export function ReliabilityPanel({
  reliability,
}: {
  reliability: ReliabilityDerivation;
}): React.ReactNode {
  return (
    <>
      <div className="sectitle">
        <h3 className="sec">Reliability</h3>
        <span className="cnt">deployment outcomes · incident adherence · SLO adherence</span>
      </div>
      <div className="grid cols-3">
        <DerivedMetric
          label="Change failure rate"
          metric={reliability.deploymentOutcomes}
          signal={(v) => v.failureRate * 100}
          render={(v) => (
            <>
              {decimal(v.failureRate * 100)} <small>% of {v.succeeded + v.failed} decided</small>
            </>
          )}
        />
        <DerivedMetric
          label="Deploys with no decisive outcome"
          metric={reliability.deploymentOutcomes}
          render={(v) => (
            <>
              {v.unknownOutcome} <small>of {v.total}</small>
            </>
          )}
        />
        <DerivedMetric
          label="Mean time to resolve"
          metric={reliability.incidentAdherence}
          render={(v) =>
            // null means nothing resolved in the window — an honest N/A inside a metric the API
            // DID derive, so it must not read as a resolve time of zero.
            v.meanTimeToResolveSeconds === null ? (
              <span className="meta">not resolved in window</span>
            ) : (
              <>
                {decimal(v.meanTimeToResolveSeconds / DAY_SECONDS)} <small>days</small>
              </>
            )
          }
        />
        <DerivedMetric
          label="Incidents"
          metric={reliability.incidentAdherence}
          render={(v) => (
            <>
              {v.resolvedCount} <small>resolved of {v.count}</small>
            </>
          )}
        />
        <DerivedMetric
          label="SLO adherence"
          metric={reliability.sloAdherence}
          render={(v) => (
            <>
              {decimal(v.observed)} <small>vs {decimal(v.target)} target</small>
            </>
          )}
        />
      </div>
    </>
  );
}

// --- Capability (E7.4 module gate; E5.5 derivation still to come) ----------
export function CapabilityPanel({
  capability,
}: {
  capability: Metric<ModuleCapabilityValue>;
}): React.ReactNode {
  return (
    <>
      <div className="sectitle">
        <h3 className="sec">Capability</h3>
        <span className="cnt">capability vs target</span>
      </div>
      <div className="grid cols-3">
        {/* "notobs", not "nodata": until E5.5 derives capability there is nothing to observe here,
            which is a different statement from "observed and empty" — and neither is a zero. */}
        <DerivedMetric
          label="Capability coverage"
          metric={capability}
          kind="notobs"
          render={(v) => (
            <>
              {v.subjectsCovered} <small>subjects over {v.factCount} facts</small>
            </>
          )}
        />
      </div>
    </>
  );
}
